import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Users, UsersRound, Search, Loader2, CheckCircle, RefreshCw, 
  ArrowRight, ArrowLeft, Play, Pause, Square, UserPlus, Shield,
  AlertTriangle, ChevronRight, Zap, Clock, Timer, CalendarClock
} from 'lucide-react';

export default function GroupMemberManagerPage() {
  // Estados de conexão
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  
  // Estados de seleção
  const [sourceGroups, setSourceGroups] = useState([]); // Grupos origem (extrair membros)
  const [targetGroup, setTargetGroup] = useState(''); // Grupo destino (adicionar membros)
  const [extractedNumbers, setExtractedNumbers] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState(new Set());
  const [numberSearchTerm, setNumberSearchTerm] = useState('');
  
  // Estados de comparação (NOVO)
  const [originalExtractedCount, setOriginalExtractedCount] = useState(0);
  const [destinationMembersCount, setDestinationMembersCount] = useState(0);
  const [filteredOutCount, setFilteredOutCount] = useState(0);
  
  // Estados de processo
  const [currentStep, setCurrentStep] = useState(1);
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [activeTask, setActiveTask] = useState(null);
  const [taskProgress, setTaskProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Estados de configuração
  const [batchSize, setBatchSize] = useState(10); // 10 por vez (máximo)
  const [delayMin, setDelayMin] = useState(120); // Delay mínimo 2 minutos
  const [delayMax, setDelayMax] = useState(180); // Delay máximo 3 minutos
  
  // Polling ref
  const pollIntervalRef = useRef(null);
  
  // Carregar conexões
  useEffect(() => {
    fetchConnections();
  }, []);
  
  // Carregar grupos quando selecionar conexão
  useEffect(() => {
    if (selectedConnection) {
      fetchGroups();
    }
  }, [selectedConnection]);
  
  // Verificar tarefa ativa ao carregar
  useEffect(() => {
    checkActiveTask();
  }, []);
  
  // Cleanup polling
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);
  
  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const connected = data.filter(c => c.status === 'connected');
        setConnections(connected);
        
        // Auto-selecionar se tiver apenas 1 conexão
        if (connected.length === 1) {
          setSelectedConnection(connected[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar conexões:', error);
    }
  };
  
  const fetchGroups = async () => {
    if (!selectedConnection) return;
    setLoadingGroups(true);
    try {
      const token = localStorage.getItem('nexus-token');
      // Usar endpoint que busca diretamente da UAZAPI para ter is_admin correto
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/groups/${selectedConnection}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
      toast.error('Erro ao buscar grupos');
    } finally {
      setLoadingGroups(false);
    }
  };
  
  const refreshGroupsFromAPI = async () => {
    if (!selectedConnection) return;
    setLoadingGroups(true);
    try {
      const token = localStorage.getItem('nexus-token');
      // Buscar diretamente da UAZAPI com force=true
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/groups/${selectedConnection}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(data.groups || []);
        toast.success('Grupos atualizados!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao atualizar grupos');
      }
    } catch (error) {
      console.error('Erro ao sincronizar grupos:', error);
      toast.error('Erro ao sincronizar');
    } finally {
      setLoadingGroups(false);
    }
  };
  
  const checkActiveTask = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-member/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.task) {
          setActiveTask(data.task);
          setCurrentStep(4);
          startProgressPolling(data.task.id);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar tarefa ativa:', error);
    }
  };
  
  const extractParticipants = async () => {
    if (sourceGroups.length === 0) {
      toast.error('Selecione grupos para extrair membros');
      return;
    }
    
    if (!targetGroup) {
      toast.error('Selecione o grupo de destino para comparação');
      return;
    }
    
    setExtracting(true);
    setExtractProgress(0);
    
    try {
      const token = localStorage.getItem('nexus-token');
      
      // 1. Extrair membros dos grupos de ORIGEM
      setExtractProgress(10);
      const sourceRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/extract-participants`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connection_id: selectedConnection,
          group_jids: sourceGroups
        })
      });
      
      if (!sourceRes.ok) {
        const error = await sourceRes.json();
        const errorMsg = typeof error.detail === 'string' 
          ? error.detail 
          : (Array.isArray(error.detail) ? error.detail[0]?.msg : 'Erro ao extrair participantes');
        toast.error(errorMsg || 'Erro ao extrair participantes dos grupos de origem');
        return;
      }
      
      setExtractProgress(40);
      const sourceData = await sourceRes.json();
      const sourceNumbers = sourceData.participants || sourceData.numbers || [];
      setOriginalExtractedCount(sourceNumbers.length);
      
      // 2. Extrair membros do grupo de DESTINO
      setExtractProgress(50);
      const destRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bulk-send/extract-participants`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connection_id: selectedConnection,
          group_jids: [targetGroup]
        })
      });
      
      setExtractProgress(70);
      let destinationNumbers = [];
      if (destRes.ok) {
        const destData = await destRes.json();
        destinationNumbers = destData.participants || destData.numbers || [];
      }
      setDestinationMembersCount(destinationNumbers.length);
      
      // 3. Comparar e filtrar - remover quem já está no destino
      setExtractProgress(85);
      const destinationSet = new Set(destinationNumbers.map(n => {
        // Normalizar número para comparação
        return n.replace('@s.whatsapp.net', '').replace('@c.us', '').replace('+', '');
      }));
      
      const filteredNumbers = sourceNumbers.filter(num => {
        const cleanNum = num.replace('@s.whatsapp.net', '').replace('@c.us', '').replace('+', '');
        return !destinationSet.has(cleanNum);
      });
      
      const removedCount = sourceNumbers.length - filteredNumbers.length;
      setFilteredOutCount(removedCount);
      
      setExtractProgress(100);
      setExtractedNumbers(filteredNumbers);
      
      // Mensagem de sucesso detalhada
      if (removedCount > 0) {
        toast.success(
          `${filteredNumbers.length} números únicos extraídos! (${removedCount} já estavam no grupo destino)`,
          { duration: 5000 }
        );
      } else {
        toast.success(`${filteredNumbers.length} números extraídos de ${sourceGroups.length} grupos!`);
      }
      
      setCurrentStep(2);
      
    } catch (error) {
      console.error('Erro ao extrair:', error);
      toast.error('Erro ao extrair participantes');
    } finally {
      setExtracting(false);
    }
  };
  
  // Função para mostrar o dialog de confirmação
  const handleStartClick = () => {
    if (!targetGroup) {
      toast.error('Selecione um grupo destino');
      return;
    }
    
    if (extractedNumbers.length === 0) {
      toast.error('Nenhum número para adicionar');
      return;
    }
    
    setShowConfirmDialog(true);
  };

  const startAddingMembers = async () => {
    setShowConfirmDialog(false);
    
    setLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-member/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connection_id: selectedConnection,
          target_group_jid: targetGroup,
          numbers: extractedNumbers,
          batch_size: batchSize,
          delay_min: delayMin,
          delay_max: delayMax
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setActiveTask(data.task);
        setCurrentStep(4);
        startProgressPolling(data.task.id);
        
        // Mostrar informação de filtro
        const filtered = data.filtered_info;
        if (filtered && filtered.skipped_already_in_group > 0) {
          toast.success(
            `Processo iniciado! ${filtered.to_add_count} números para adicionar (${filtered.skipped_already_in_group} já estavam no grupo)`,
            { duration: 5000 }
          );
        } else {
          toast.success('Processo iniciado!');
        }
      } else {
        try {
          const error = await res.json();
          toast.error(error.detail || 'Erro ao iniciar');
        } catch {
          toast.error(`Erro do servidor: ${res.status}`);
        }
      }
    } catch (error) {
      console.error('Erro ao iniciar:', error);
      toast.error('Erro ao iniciar processo');
    } finally {
      setLoading(false);
    }
  };
  
  const startProgressPolling = (taskId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    const poll = async () => {
      try {
        const token = localStorage.getItem('nexus-token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-member/${taskId}/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTaskProgress(data);
          setActiveTask(prev => prev ? { ...prev, status: data.status } : null);
          
          if (data.status === 'completed' || data.status === 'cancelled' || data.status === 'error') {
            clearInterval(pollIntervalRef.current);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar progresso:', error);
      }
    };
    
    poll();
    pollIntervalRef.current = setInterval(poll, 3000);
  };
  
  const pauseTask = async () => {
    if (!activeTask) return;
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-member/${activeTask.id}/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActiveTask(prev => prev ? { ...prev, status: 'paused' } : null);
        toast.success('Pausado!');
      }
    } catch (error) {
      toast.error('Erro ao pausar');
    }
  };
  
  const resumeTask = async () => {
    if (!activeTask) return;
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-member/${activeTask.id}/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActiveTask(prev => prev ? { ...prev, status: 'running' } : null);
        startProgressPolling(activeTask.id);
        toast.success('Retomado!');
      }
    } catch (error) {
      toast.error('Erro ao retomar');
    }
  };
  
  const cancelTask = async () => {
    if (!activeTask) return;
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-member/${activeTask.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActiveTask(prev => prev ? { ...prev, status: 'cancelled' } : null);
        toast.success('Cancelado!');
      }
    } catch (error) {
      toast.error('Erro ao cancelar');
    }
  };
  
  const forceResetTasks = async () => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/group-member/reset-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Tarefas resetadas!');
        resetAll();
      } else {
        toast.error('Erro ao resetar tarefas');
      }
    } catch (error) {
      console.error('Erro ao resetar:', error);
      toast.error('Erro ao resetar tarefas');
    }
  };
  
  const resetAll = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    setSourceGroups([]);
    setTargetGroup('');
    setExtractedNumbers([]);
    setSelectedNumbers(new Set());
    setActiveTask(null);
    setTaskProgress(null);
    setCurrentStep(1);
  };
  
  const toggleSourceGroup = (groupId) => {
    if (sourceGroups.includes(groupId)) {
      setSourceGroups(sourceGroups.filter(g => g !== groupId));
    } else {
      setSourceGroups([...sourceGroups, groupId]);
    }
  };
  
  const removeNumber = (num) => {
    setExtractedNumbers(prev => prev.filter(n => n !== num));
  };
  
  const filteredGroups = groups.filter(g => 
    g.name?.toLowerCase().includes(groupSearch.toLowerCase())
  );
  
  const filteredNumbers = extractedNumbers.filter(n =>
    n.includes(numberSearchTerm)
  );
  
  // Todos os grupos disponíveis para destino (removida restrição de admin)
  const availableGroups = groups;
  
  // Helper para obter o ID do grupo (compatível com diferentes formatos)
  const getGroupId = (group) => group.group_id || group.jid || group.id;
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'bg-blue-500';
      case 'paused': return 'bg-amber-500';
      case 'scheduled': return 'bg-purple-500';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-gray-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'running': return 'Executando';
      case 'paused': return 'Pausado';
      case 'scheduled': return 'Agendado';
      case 'completed': return 'Concluído';
      case 'cancelled': return 'Cancelado';
      case 'error': return 'Erro';
      default: return status;
    }
  };
  
  // Formatar data/hora do próximo slot
  const formatNextSlot = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return date.toLocaleString('pt-BR', { 
        day: '2-digit', 
        month: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return null;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-2">
            <UserPlus className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
            Extrair | Enviar
          </h1>
          <p className="text-xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">
            Extraia membros de grupos e adicione em outro grupo
          </p>
        </div>
        
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={forceResetTasks}
            className="text-xs border-red-500/50 text-red-600 hover:bg-red-500/10"
            title="Resetar tarefas travadas"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Reset
          </Button>
        </div>
      </div>
      
      {/* Progress Steps */}
      {currentStep < 4 && (
        <div className="flex items-center justify-between sm:justify-center gap-1 sm:gap-2 p-3 sm:p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/20 overflow-x-auto">
          {[
            { step: 1, label: 'Extrair', icon: Users },
            { step: 2, label: 'Números', icon: CheckCircle },
            { step: 3, label: 'Adicionar', icon: UserPlus }
          ].map((item, index) => (
            <div key={item.step} className="flex items-center shrink-0">
              <div className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all ${
                currentStep >= item.step 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                <item.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium">{item.label}</span>
              </div>
              {index < 2 && <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 mx-0.5 sm:mx-2 text-muted-foreground" />}
            </div>
          ))}
        </div>
      )}

      {/* Step 1: Selecionar Grupos Origem */}
      {currentStep === 1 && (
        <div className="space-y-4 sm:space-y-6">
          {/* Seleção de Conexão */}
          <Card>
            <CardHeader className="pb-3 sm:pb-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <UsersRound className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                Selecione a Conexão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                  <SelectTrigger className="w-full sm:flex-1" data-testid="connection-select">
                    <SelectValue placeholder="Selecione uma conexão" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map(conn => (
                      <SelectItem key={conn.id} value={conn.id}>
                        {conn.name || conn.instance_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={refreshGroupsFromAPI} 
                  disabled={!selectedConnection || loadingGroups}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  {loadingGroups ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Atualizar
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Lista de Grupos - Mostra sempre após selecionar conexão */}
          {selectedConnection && (
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                    Grupos de Extração ({groups.length})
                  </CardTitle>
                  <Badge variant="outline" className="bg-blue-500/20 text-xs self-start sm:self-auto">
                    {sourceGroups.length} selecionados
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {loadingGroups ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-2" />
                    <p className="text-sm text-muted-foreground">Carregando grupos...</p>
                  </div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <UsersRound className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhum grupo encontrado</p>
                    <p className="text-xs mt-1">Clique em "Atualizar" para sincronizar os grupos</p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar grupos..."
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                        className="pl-10 text-sm"
                      />
                    </div>
                    
                    <div className="max-h-[250px] sm:max-h-[300px] overflow-y-auto space-y-2">
                      {filteredGroups.map(group => {
                        const groupId = getGroupId(group);
                        const isDestination = targetGroup === groupId;
                        return (
                        <div
                          key={groupId}
                          className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border cursor-pointer transition-all ${
                            isDestination 
                              ? 'border-gray-400 bg-gray-500/10 opacity-50 cursor-not-allowed'
                              : sourceGroups.includes(groupId)
                                ? 'border-blue-500 bg-blue-500/10'
                                : 'border-border hover:border-blue-500/50 hover:bg-muted/50'
                          }`}
                          onClick={() => !isDestination && toggleSourceGroup(groupId)}
                        >
                          <Checkbox 
                            checked={sourceGroups.includes(groupId)} 
                            disabled={isDestination}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm sm:text-base truncate">
                              {group.name}
                              {isDestination && <span className="text-gray-500 ml-2">(destino)</span>}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {group.participants_count} membros
                              {group.is_admin && <span className="text-blue-500 ml-1">• Admin</span>}
                            </p>
                          </div>
                          <Badge variant="outline" className="shrink-0 text-xs">
                            {group.participants_count}
                          </Badge>
                        </div>
                      )})}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Grupo Destino - NOVO */}
          {selectedConnection && groups.length > 0 && (
            <Card>
              <CardHeader className="pb-3 sm:pb-6">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                  Grupo de Destino (para comparação)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Selecione o grupo onde deseja adicionar os membros. 
                  Os números que já estiverem neste grupo serão <strong>automaticamente removidos</strong> da lista de extração.
                </p>
                
                <Select value={targetGroup} onValueChange={(value) => {
                  setTargetGroup(value);
                  // Remover do sourceGroups se estiver lá
                  if (sourceGroups.includes(value)) {
                    setSourceGroups(prev => prev.filter(g => g !== value));
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o grupo destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableGroups.map(group => {
                      const groupId = getGroupId(group);
                      return (
                      <SelectItem key={groupId} value={groupId}>
                        {group.name} ({group.participants_count} membros) {group.is_admin && '⭐'}
                      </SelectItem>
                    )})}
                  </SelectContent>
                </Select>
                
                {/* Botão de Extrair */}
                <Button
                  onClick={extractParticipants}
                  disabled={sourceGroups.length === 0 || !targetGroup || extracting}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 h-10 sm:h-12 text-sm sm:text-lg"
                >
                  {extracting ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mr-2" />
                      Extraindo e Comparando... {extractProgress.toFixed(0)}%
                    </>
                  ) : (
                    <>
                      <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Extrair e Comparar ({sourceGroups.length} grupos)
                    </>
                  )}
                </Button>
                
                {extracting && <Progress value={extractProgress} className="h-2" />}
                
                {!targetGroup && sourceGroups.length > 0 && (
                  <p className="text-xs text-amber-600 text-center">
                    ⚠️ Selecione o grupo destino para continuar
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Step 2: Números Extraídos */}
      {currentStep === 2 && (
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
              Números Extraídos e Filtrados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Resumo da comparação */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="p-2 sm:p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-blue-600">{originalExtractedCount}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Extraídos</p>
              </div>
              <div className="p-2 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-red-600">{filteredOutCount}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Já no destino</p>
              </div>
              <div className="p-2 sm:p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-green-600">{extractedNumbers.length}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Únicos</p>
              </div>
            </div>
            
            {filteredOutCount > 0 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-xs sm:text-sm text-amber-700">
                  ✅ <strong>{filteredOutCount}</strong> números já estavam no grupo destino e foram removidos automaticamente!
                </p>
              </div>
            )}
            
            {/* Busca */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar número..."
                value={numberSearchTerm}
                onChange={(e) => setNumberSearchTerm(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
            
            {/* Lista de números */}
            <div className="max-h-[200px] sm:max-h-[300px] overflow-y-auto space-y-1 p-2 bg-muted/50 rounded-lg">
              {filteredNumbers.slice(0, 100).map((num, i) => (
                <div key={i} className="flex items-center justify-between text-xs sm:text-sm py-1.5 px-2 bg-background rounded">
                  <span className="font-mono">{num}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeNumber(num)}
                    className="h-6 w-6 p-0 text-red-500"
                  >
                    ×
                  </Button>
                </div>
              ))}
              {filteredNumbers.length > 100 && (
                <p className="text-xs text-center text-muted-foreground py-2">
                  ... e mais {filteredNumbers.length - 100} números
                </p>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(1)} className="w-full sm:flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <Button 
                onClick={() => setCurrentStep(3)} 
                disabled={extractedNumbers.length === 0}
                className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-cyan-600"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Continuar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Configurar e Iniciar */}
      {currentStep === 3 && (
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              Configurar e Iniciar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Resumo - Grupo destino já selecionado */}
            <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm">
                <strong>{extractedNumbers.length}</strong> números serão adicionados ao grupo destino
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Grupo: {groups.find(g => getGroupId(g) === targetGroup)?.name || 'Selecionado'}
              </p>
            </div>
            
            {/* Aviso importante */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm">
                  <p className="font-medium text-amber-600">⚠️ IMPORTANTE: A conexão será desfeita!</p>
                  <ul className="text-muted-foreground mt-1 space-y-0.5">
                    <li>• Máximo <strong>10 membros</strong> por requisição</li>
                    <li>• <strong>2-3 minutos</strong> de delay entre requisições</li>
                    <li>• <strong>Reconecte o WhatsApp</strong> após o processo</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Configuração de Batch */}
            <div className="space-y-2">
              <Label className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                Membros por vez
              </Label>
              <Select value={batchSize.toString()} onValueChange={(v) => setBatchSize(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 3, 5, 10].map(n => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} membro{n > 1 ? 's' : ''} por vez
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Configurações de Delay */}
            <div className="space-y-2">
              <Label className="text-sm flex items-center gap-2">
                <Timer className="w-4 h-4" />
                Delay entre batches (segundos)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Mínimo</Label>
                  <Input
                    type="number"
                    value={delayMin}
                    onChange={(e) => {
                      const val = Math.max(60, Math.min(180, parseInt(e.target.value) || 60));
                      setDelayMin(val);
                      if (delayMax < val + 30) setDelayMax(val + 30);
                    }}
                    min={60}
                    max={180}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Máximo</Label>
                  <Input
                    type="number"
                    value={delayMax}
                    onChange={(e) => setDelayMax(Math.max(delayMin + 30, Math.min(300, parseInt(e.target.value) || 90)))}
                    min={delayMin + 30}
                    max={300}
                    className="h-9"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Delay aleatório entre {delayMin}s e {delayMax}s entre cada membro (mínimo 60s)
              </p>
            </div>
            
            {/* Preview */}
            <div className="p-3 bg-muted/50 rounded-lg text-xs sm:text-sm space-y-1">
              <p className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-blue-500" />
                <strong>Estimativa:</strong>
              </p>
              <p>• <strong>Modo:</strong> {batchSize} membro{batchSize > 1 ? 's' : ''} por vez</p>
              <p>• <strong>Delay:</strong> 2-3 minutos entre cada requisição</p>
              <p>• <strong>Total:</strong> {extractedNumbers.length} membros</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={() => setCurrentStep(2)} className="w-full sm:flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <Button 
                onClick={handleStartClick}
                disabled={!targetGroup || loading}
                className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-cyan-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Iniciando...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Iniciar Adição
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Progresso */}
      {currentStep === 4 && activeTask && (
        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Zap className="w-5 h-5 text-blue-500" />
                Adicionando Membros
              </CardTitle>
              <Badge className={`${getStatusColor(taskProgress?.status || activeTask.status)} text-white text-xs`}>
                {getStatusText(taskProgress?.status || activeTask.status)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Barra de progresso */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs sm:text-sm">
                <span>Progresso</span>
                <span className="font-bold">{taskProgress?.progress_percent?.toFixed(0) || 0}%</span>
              </div>
              <Progress value={taskProgress?.progress_percent || 0} className="h-3 sm:h-4" />
            </div>
            
            {/* Estatísticas */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
              <div className="p-2 sm:p-4 bg-muted/50 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold">{taskProgress?.total || activeTask.total_numbers}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Total</p>
              </div>
              <div className="p-2 sm:p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-green-600">{taskProgress?.added || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Adicionados</p>
              </div>
              <div className="p-2 sm:p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-red-600">{taskProgress?.failed || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Falhas</p>
              </div>
              <div className="p-2 sm:p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                <p className="text-lg sm:text-2xl font-bold text-blue-600">{taskProgress?.pending || 0}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Pendentes</p>
              </div>
            </div>
            
            {/* Info de filtro - membros já no grupo */}
            {activeTask.skipped_already_in_group > 0 && (
              <div className="p-2 sm:p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-xs sm:text-sm text-cyan-700 dark:text-cyan-300">
                  ⏭️ <strong>{activeTask.skipped_already_in_group}</strong> números foram ignorados pois já estavam no grupo destino
                  <span className="text-muted-foreground ml-1">
                    (de {activeTask.original_numbers_count} extraídos)
                  </span>
                </p>
              </div>
            )}
            
            {/* Info do slot - ATUALIZADO */}
            <div className="p-2 sm:p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-1">
              <p className="text-xs sm:text-sm text-purple-700 dark:text-purple-300 flex items-center gap-2">
                <CalendarClock className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
                <strong>Slot {(taskProgress?.slots_completed || 0) + 1}</strong> de {taskProgress?.estimated_slots || Math.ceil((taskProgress?.total || activeTask.total_numbers) / 100)}
                {taskProgress?.current_batch > 0 && (
                  <span className="text-muted-foreground">
                    | Batch {taskProgress.current_batch}/{taskProgress.total_batches || '?'}
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                Batch: {activeTask.batch_size || 3} por vez | Delay: {taskProgress?.delay_min || activeTask.delay_min || 60}s - {taskProgress?.delay_max || activeTask.delay_max || 90}s
              </p>
              {taskProgress?.last_slot_added > 0 && (
                <p className="text-xs text-muted-foreground">
                  Último slot: {taskProgress.last_slot_added} adicionados
                </p>
              )}
            </div>
            
            {/* Status de agendamento */}
            {(taskProgress?.status === 'scheduled' || activeTask.status === 'scheduled') && taskProgress?.next_slot_at && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500 animate-pulse" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-600">Próximo slot agendado</p>
                    <p className="text-muted-foreground">{formatNextSlot(taskProgress.next_slot_at)}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Aviso de falhas */}
            {taskProgress?.slot_failures > 0 && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm">
                    <p className="font-medium text-amber-600">
                      {taskProgress.slot_failures} slot(s) com falhas consecutivas (máx 5)
                    </p>
                    {taskProgress.last_failure_reason && (
                      <p className="text-muted-foreground">{taskProgress.last_failure_reason}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Motivo de cancelamento */}
            {(taskProgress?.status === 'cancelled' || activeTask.status === 'cancelled') && taskProgress?.cancel_reason && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm">
                    <p className="font-medium text-red-600">Motivo do cancelamento</p>
                    <p className="text-muted-foreground">{taskProgress.cancel_reason}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Controles */}
            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
              {(taskProgress?.status === 'running' || activeTask.status === 'running') && (
                <Button
                  variant="outline"
                  onClick={pauseTask}
                  className="w-full sm:flex-1 h-9 sm:h-10 text-sm"
                >
                  <Pause className="w-4 h-4 mr-1 sm:mr-2" />
                  Pausar
                </Button>
              )}
              
              {(taskProgress?.status === 'scheduled' || activeTask.status === 'scheduled') && (
                <Button
                  variant="outline"
                  onClick={pauseTask}
                  className="w-full sm:flex-1 h-9 sm:h-10 text-sm border-amber-500 text-amber-600"
                >
                  <Pause className="w-4 h-4 mr-1 sm:mr-2" />
                  Pausar Agendamento
                </Button>
              )}
              
              {(taskProgress?.status === 'paused' || activeTask.status === 'paused') && (
                <Button
                  onClick={resumeTask}
                  className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 h-9 sm:h-10 text-sm"
                >
                  <Play className="w-4 h-4 mr-1 sm:mr-2" />
                  Retomar
                </Button>
              )}
              
              {!['completed', 'cancelled', 'error'].includes(taskProgress?.status || activeTask.status) && (
                <Button
                  variant="destructive"
                  onClick={cancelTask}
                  className="w-full sm:flex-1 h-9 sm:h-10 text-sm"
                >
                  <Square className="w-4 h-4 mr-1 sm:mr-2" />
                  Cancelar
                </Button>
              )}
              
              {['completed', 'cancelled', 'error'].includes(taskProgress?.status || activeTask.status) && (
                <Button
                  onClick={resetAll}
                  className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 h-9 sm:h-10 text-sm col-span-2 sm:col-span-1"
                >
                  <RefreshCw className="w-4 h-4 mr-1 sm:mr-2" />
                  Nova Tarefa
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Confirmação */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Atenção - Aviso Importante!
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="text-base font-medium text-foreground">
                A conexão do WhatsApp será desfeita após processar este pedido!
              </p>
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                <p className="text-amber-700 dark:text-amber-400">
                  Devido às políticas do WhatsApp, operações em massa de adição de membros 
                  geralmente causam a desconexão da sessão. Você precisará reconectar o WhatsApp 
                  após a conclusão do processo.
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                <strong>{extractedNumbers.length}</strong> membros serão adicionados ao grupo selecionado.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={startAddingMembers}
              className="bg-gradient-to-r from-blue-600 to-cyan-600"
            >
              Entendi, Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
